import { resolveApiUrl } from "~/utils/url/urlResolver";
import { SearchParamBuilder } from "~/utils/searchParamBuilder";
import { type dtoSalesPartnerData } from "~/domains/salesPartner/types/SalesPartnerTypes";

export enum SearchType {
  Partner = "partner",
  Address = "address",
  CertificateID = "certificateID",
}

export interface InputsData {
  query: string;
  radius: number;
  filterBySalesPartner: boolean;
  searchType: SearchType;
  productCompetence?: string[];
  certifiedCompetence?: string[];
  countryCode: string;
}

export interface SalesPartnerSearchQuery {
  query: string;
  radius: number;
  searchType: SearchType;
  productCompetence: string[];
  certifiedCompetence: string[];
  countryCode: string;
  lat: number | null;
  lng: number | null;
  preferredOnly: boolean;
}

export type UrlParams = Pick<
  InputsData,
  "query" | "radius" | "countryCode" | "searchType" | "productCompetence" | "certifiedCompetence"
> & {
  preferredSalesPartner?: string;
  lat?: number;
  lng?: number;
  salesPartnerId?: string;
};

/**
 * Error class for API errors with HTTP status code
 */
export class SalesPartnerApiError extends Error {
  public readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? `API error: ${status}`);
    this.name = "SalesPartnerApiError";
    this.status = status;
  }
}

export async function getSalesPartnerSearch(
  inputsData: UrlParams,
  pageNumber?: number,
  abortSignal?: AbortSignal,
): Promise<dtoSalesPartnerData> {
  const url = createUrlFromInputValues(inputsData, pageNumber);
  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    mode: "cors",
    referrer: "no-referrer",
    headers: {
      dataType: "json",
      "content-type": "application/json",
    },
    signal: abortSignal,
  });

  if (!response.ok) {
    throw new SalesPartnerApiError(response.status, `Error status: ${response.status}`);
  }

  return response.json();
}

export function createUrlFromInputValues(inputsData: UrlParams, pageNumber?: number): string {
  const builder = new SearchParamBuilder();

  builder.setIfDefined("searchType", inputsData.searchType);
  builder.setIfDefined("q", inputsData.query);

  if (inputsData.searchType === SearchType.Address && inputsData.radius !== -1) {
    builder.setIfDefined("radius", inputsData.radius);
  }

  builder.setIfDefined("latitude", inputsData.lat);
  builder.setIfDefined("longitude", inputsData.lng);

  if (!inputsData.salesPartnerId) {
    builder.setIfDefined("country", inputsData.countryCode);
  }

  builder.setIfDefined("preferredSalesPartner", inputsData.salesPartnerId);
  builder.setIfDefined("page", pageNumber);

  builder.appendArrayParam("productCompetence", inputsData.productCompetence);
  builder.appendArrayParam("certifiedCompetence", inputsData.certifiedCompetence);

  return resolveApiUrl(`/store-finder/search?${builder.toString()}`);
}
